/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { catchError, map, Observable, of } from "rxjs";
import {
	AdvancedFilters,
	EnvironmentalImpactByApplication
} from "../../models/http/application/environmental-impact-by-application.model";
import { LineChart } from "../../models/http/charts/line-chart/line-chart.model";
import {
	FilterApplication,
	FilterPageResponse,
	FilterRequest
} from "../../models/http/dashboard/filter-page-response.model";
import { KPI } from "../../models/http/dashboard/kpi.model";
import { TotalMonths } from "../../models/http/dashboard/total-months.model";
import { ChartResponse } from "../../models/ui/charts.model";
import { HttpMethods } from "../../models/ui/http-methods.model";
import { ENDPOINT_GROUPS } from "../../utils/apis/apis.constants";
import { CommunicationService } from "../communication.service";

@Injectable({
	providedIn: "root"
})
export class DashboardSoftwareAssetsHttpService {
	private _dashboardSoftwareAssetsOperations = ENDPOINT_GROUPS.dashboardSoftwareAssets.operations;

	constructor(private _communicationService: CommunicationService) {}

	/**
	 * Retrieves CO2 emissions data for each app for a line chart within the specified date range .
	 *
	 * @param filters - The AdvancedFilters request body.
	 * @returns An Observable that emits the line chart data for CO2 emissions.
	 */
	public getAppsPerformance(filters: AdvancedFilters): Observable<ChartResponse<LineChart>> {
		return this._communicationService
			.callService(
				this._dashboardSoftwareAssetsOperations.getAppsPerformance.service,
				HttpMethods.POST,
				{},
				filters
			)
			.pipe(
				map((response: LineChart) => ({ chart: response, hasError: false })),
				catchError(() => of({ chart: {} as LineChart, hasError: true }))
			);
	}

	public getAppsWaterConsumption(
		startDate: string,
		endDate: string,
		applicationId: string[],
		layerId: string[],
		tenantId: string
	): Observable<ChartResponse<LineChart>> {
		const params = new HttpParams({
			fromObject: {
				startDate,
				endDate,
				applicationId,
				layerId,
				tenantId
			}
		});

		return this._communicationService
			.callService(
				this._dashboardSoftwareAssetsOperations.getAppsWaterConsumption.service,
				HttpMethods.GET,
				{ params }
			)
			.pipe(
				map((response: LineChart) => ({ chart: response, hasError: false })),
				catchError(() => of({ chart: {} as LineChart, hasError: true }))
			);
	}

	/**
	 * Retrieves the total CO2 KPI (Key Performance Indicator).
	 *
	 * @param filters - The AdvancedFilters request body.
	 * @returns An Observable that emits the KPI data.
	 */
	public getTotalCO2(filters: AdvancedFilters): Observable<KPI> {
		return this._communicationService.callService(
			this._dashboardSoftwareAssetsOperations.getTotalCo2.service,
			HttpMethods.POST,
			{},
			filters
		);
	}

	public getTotalEmissions(startDate: string, endDate?: string): Observable<TotalMonths> {
		const params = new HttpParams({
			fromObject: {
				startDate,
				...(endDate && { endDate })
			}
		});

		return this._communicationService.callService(
			this._dashboardSoftwareAssetsOperations.getTotalEmissions.service,
			HttpMethods.GET,
			{ params }
		);
	}

	/**
	 * Exports the response data in CSV format.
	 *
	 * @param filters - The AdvancedFilters payload as defined in the cursor-based filters API spec.
	 * @returns An observable that emits the response from the export service.
	 */
	public exportResponseInCsv(filters: AdvancedFilters): Observable<Blob> {
		return this._communicationService.callService(
			this._dashboardSoftwareAssetsOperations.exportCsv.service,
			HttpMethods.POST,
			{ responseType: "blob" },
			filters
		);
	}

	/**
	 * Exports the response data in CSV format for the specified date range, application IDs, layer IDs, and tenant ID.
	 *
	 * @param startDate - The start date for the data export in YYYY-MM-DD format.
	 * @param endDate - The end date for the data export in YYYY-MM-DD format.
	 * @param applicationId - An array of application IDs to filter the data.
	 * @param layerId - An array of layer IDs to filter the data.
	 * @param tenantId - The tenant ID to filter the data.
	 * @returns An observable that emits the response from the export service.
	 */
	public exportResponseManifest(
		startDate: string,
		endDate: string,
		applicationId: string,
		tenantId: string
	): Observable<Blob> {
		const params = new HttpParams({
			fromObject: {
				startDate,
				endDate,
				applicationId,
				tenantId
			}
		});

		return this._communicationService.callService(
			this._dashboardSoftwareAssetsOperations.exportManifest.service,
			HttpMethods.POST,
			{ params, responseType: "blob" }
		);
	}

	// ── Filter endpoints ───────────────────────────────────────────────────────

	/**
	 * GET /api/v1/dashboard/users/filters/applications-ids
	 * Paginated list of application IDs. No body, no allSelected.
	 */
	public getApplicationsIds(
		search: string,
		cursor: string | null,
		size: number
	): Observable<FilterPageResponse<FilterApplication>> {
		let params = new HttpParams().set("size", size);
		if (search) params = params.set("search", search);
		if (cursor) params = params.set("cursor", cursor);

		return this._communicationService.callService(
			this._dashboardSoftwareAssetsOperations.getApplicationsIds.service,
			HttpMethods.GET,
			{ params }
		);
	}

	/**
	 * POST /api/v1/dashboard/users/filters/layer-ids
	 * Paginated list of layer IDs scoped to selected applications.
	 * allSelected is a required query parameter; selectedIds and excludedIds go in the body.
	 */
	public getLayerIds(
		body: FilterRequest,
		search: string,
		cursor: string | null,
		size: number
	): Observable<FilterPageResponse<string>> {
		const { allSelected, ...bodyPayload } = body;
		let params = new HttpParams().set("size", size).set("allSelected", allSelected);
		if (search) params = params.set("search", search);
		if (cursor) params = params.set("cursor", cursor);

		return this._communicationService.callService(
			this._dashboardSoftwareAssetsOperations.getLayerIds.service,
			HttpMethods.POST,
			{ params },
			bodyPayload
		);
	}

	public getEnvironmentalImpactByApplication(
		filters: AdvancedFilters
	): Observable<EnvironmentalImpactByApplication> {
		return this._communicationService.callService(
			this._dashboardSoftwareAssetsOperations.getEnvironmentalImpactByApplication.service,
			HttpMethods.POST,
			{},
			filters
		);
	}
}
